import { Component, Fragment, h, Prop, Element, Listen } from '@stencil/core';
import { initShadowMountPoints } from '../../../utils/utils';
import { GiftCard, PaymentMethod, RtrError } from '../../../redux/types';
import { ErrorType, hasError } from '../../rtr-errors/utils';
import { PaymentOption } from '../utils/types';
import { SelectOption } from '../../../types/types';

@Component({
  tag: 'rtr-payment-method',
  styleUrl: '../style.sass',
  shadow: true,
})
export class RtrPaymentMethod {
  @Prop() orderReference: string;
  @Prop() paymentOptions;
  @Prop() currentMethod: PaymentMethod;
  @Prop() secondaryPaymentMethod: PaymentMethod;
  @Prop() onSepaNameChange;
  @Prop() showPaymentOptions;
  @Prop() prepaymentPasscodeEnabled: boolean;
  @Prop() validationOptionsError: boolean;
  @Prop() validationMethodError: boolean;
  @Prop() giftCard: GiftCard;
  @Prop() errors: RtrError[] = [];
  @Prop() onBlikCodeChange: (code: string) => void;
  @Prop() onSecondaryOptionChange: (item: SelectOption) => void;

  @Element() methodEl: HTMLElement;
  private refEl: HTMLElement;

  componentDidLoad() {
    initShadowMountPoints(this.methodEl, 'stripe-sepa-iban');
    initShadowMountPoints(this.methodEl, 'stripe-cc-cardNumber');
    initShadowMountPoints(this.methodEl, 'stripe-cc-cardExpiry');
    initShadowMountPoints(this.methodEl, 'stripe-cc-cardCvc');
    initShadowMountPoints(this.methodEl, 'paypal-plus');

    if (this.methodsHasError || this.validationMethodError) {
      this.refEl?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  @Listen('rtrPaymentMethodsNotValidated', { capture: true, target: 'body' })
  async scrollToError() {
    this.refEl?.scrollIntoView({ behavior: 'smooth' });
  }

  get methodsHasError(): boolean {
    return hasError({ type: ErrorType.PaymentProblemWithMethod, errors: this.errors });
  }

  get methodClass() {
    if (this.validationMethodError) {
      return 'RTPOptionsContainer RTPOptionError';
    }

    return 'RTPOptionsContainer';
  }

  render() {
    if (this.showPaymentOptions) {
      return false;
    }

    return (
      <div class={this.methodClass} ref={el => (this.refEl = el)}>
        {this.currentMethod?.value === 'stripe-cc' && (
          <Fragment>
            <rentware-spacer size="L" />

            <div class="RTPTitle">Credit card details</div>

            <rentware-spacer size="S" />

            <div class="RTPItemContainer">
              <slot name="stripe-cc-cardNumber" />
            </div>
            <div class="RTPItemContainer RTPItemContainerHalf">
              <slot name="stripe-cc-cardExpiry" />
            </div>
            <div class="RTPItemContainer RTPItemContainerHalf">
              <slot name="stripe-cc-cardCvc" />
            </div>
          </Fragment>
        )}

        {this.currentMethod?.value === 'paypal-plus' && <slot name="paypal-plus" />}

        {this.currentMethod?.value === 'prepayment' && <rtr-payment-prepayment withCode={this.prepaymentPasscodeEnabled} />}

        {this.currentMethod?.value === 'giftcard' && (
          <rtr-payment-giftcard
            orderReference={this.orderReference}
            giftCard={this.giftCard}
            paymentOptions={this.paymentOptions}
            onSepaNameChange={this.onSepaNameChange}
            secondaryPaymentMethod={this.secondaryPaymentMethod}
            errors={this.errors}
            validationOptionsError={this.validationOptionsError}
            onSecondaryOptionChange={this.onSecondaryOptionChange}
          />
        )}

        {this.currentMethod?.value === 'stripe-sepa' && (
          <Fragment>
            <rentware-spacer size="L" />

            <div class="RTPTitle">SEPA details</div>

            <rentware-spacer size="S" />

            <div class="RTPItemContainer">
              <input
                class="RTPInputSEPA"
                id="account-holder-name"
                name="account-holder-name"
                placeholder="Name"
                required
                onInput={(event: Event) => this.onSepaNameChange((event.target as any).value)}
              />
            </div>

            <div class="RTPItemContainer">
              <slot name="stripe-sepa-iban" />
            </div>
          </Fragment>
        )}

        {this.currentMethod?.value === PaymentOption.StripeBlik && <rtr-payment-blik onBlikCodeChange={this.onBlikCodeChange} />}
      </div>
    );
  }
}
